// Modal.tsx
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '@mui/material/styles';
import { CustomTheme } from '../../../../context/PermissionsContext/ThemeContext/themesTypes';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: number;
  noScroll?: boolean;
}

const Overlay = styled.div<{ isDark?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ isDark }) =>
    isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  backdrop-filter: blur(2px);
`;

const ModalWrapper = styled.div<{
  width: number;
  noScroll?: boolean;
  theme?: CustomTheme;
  isDark?: boolean;
}>`
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : '#fff')};
  padding: 20px;
  width: ${({ width }) => `${width}px`};
  border-radius: 12px;
  border: ${({ theme, isDark }) =>
    isDark ? `1px solid ${theme?.custom?.stroke || '#404040'}` : 'none'};
  box-shadow: ${({ isDark }) =>
    isDark
      ? 'rgba(0, 0, 0, 0.5) 0px 8px 32px 0px'
      : 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px'};
  max-height: 80vh;
  height: 80vh;
  overflow: ${({ noScroll }) => (noScroll ? 'hidden' : 'auto')};
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease-in-out;
`;

const Title = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  font-size: 24px;
  font-family: Space Grotesk;
  font-weight: 700;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#333333')};
`;

const Header = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f0f0f0')};
`;

const CloseButton = styled.img<{ isDark?: boolean }>`
  width: 18px;
  height: 18px;
  cursor: pointer;
  filter: ${({ isDark }) => (isDark ? 'invert(1)' : 'none')};
  transition: filter 0.2s ease, opacity 0.2s ease;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const ModalContent = styled.div<{
  theme?: CustomTheme;
  isDark?: boolean;
  noScroll?: boolean;
}>`
  flex: 1;
  overflow: ${({ noScroll }) => (noScroll ? 'hidden' : 'auto')};
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#333333')};

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme, isDark }) =>
      theme?.custom?.background3 || (isDark ? '#2D2D2D' : '#f8f9fa')};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme, isDark }) =>
      theme?.custom?.textLight || (isDark ? '#555555' : '#dee2e6')};
    border-radius: 4px;

    &:hover {
      background: ${({ theme, isDark }) =>
        theme?.custom?.text || (isDark ? '#666666' : '#adb5bd')};
    }
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme, isDark }) =>
    isDark
      ? `${theme?.custom?.textLight || '#555555'} ${
          theme?.custom?.background3 || '#2D2D2D'
        }`
      : `${theme?.custom?.textLight || '#dee2e6'} ${
          theme?.custom?.background3 || '#f8f9fa'
        }`};
`;

const CustomModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = 500,
  noScroll = false,
}) => {
  const theme = useTheme();
  const isDark = theme.custom?.isDark || false;

  if (!isOpen) return null;

  return (
    <Overlay isDark={isDark} onClick={() => onClose()}>
      <ModalWrapper
        width={width}
        noScroll={noScroll}
        theme={theme as any}
        isDark={isDark}
        onClick={(e) => e.stopPropagation()}
      >
        <Header theme={theme as any} isDark={isDark}>
          <Title theme={theme as any} isDark={isDark}>
            {title}
          </Title>
          <CloseButton
            src="/images/close-light.svg"
            isDark={isDark}
            onClick={() => onClose()}
          />
        </Header>
        <ModalContent theme={theme as any} isDark={isDark} noScroll={noScroll}>
          {children}
        </ModalContent>
      </ModalWrapper>
    </Overlay>
  );
};

export default CustomModal;
